import { API } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { FacilityAndSpaceFeatures, StructuredActionTaken, UnitFilters, UnitOption, UnitPickerData, findFeatureDef } from "@swivl-mono/types";
import React, { useEffect, useState } from "react";

import { formatCurrency } from "../../Helpers/formatCurrency";
import { formatTime } from "../../Helpers/formatTime";
import type { ChatMessage } from "../../Types/BotChat.Types";
import { t } from "../../i18n/i18n";
import { UnitThumbnail, unitImageKindFor } from "../Reservation/UnitThumbnail";

interface UnitPickerProps {
  data: UnitPickerData;
  message: ChatMessage;
  /** Called when the user taps a unit card. Wired to the socket via ChatWindow. */
  onAction?: (action: StructuredActionTaken) => void;
  /** Read-only mode for the agent timeline. No tap handlers; renders selected state if actionTaken. */
  readOnly?: boolean;
  /**
   * Push a new `payload.data` after picker-API filter refresh. See widget
   * design doc §A4.6.
   */
  onUpdateData?: (data: Record<string, unknown>) => void;
  /** Org context — required for picker-API calls. */
  orgId?: number;
  /** Conversation id for picker-API debug logging. */
  conversationId?: number | null;
}

/**
 * `unit-picker` structured-message renderer. Shows unit cards; tapping one
 * fires `onAction` with a `selected` action targeting that unit's id. When
 * `message.actionTaken` is present, renders in selected state.
 *
 * Filter UI: opens a bottom-sheet modal (per Figma node 207-17738) where
 * the user picks Unit Size + Amenities + Sort. On Apply, the widget calls
 * the embed-picker API directly and merges the new units into this same
 * message via `onUpdateData`.
 *
 * See:
 *   docs/snowy/widget/2024-04-29-new-chat-widget-design.md §2.3, §3
 *   /Users/chuck/.claude/plans/ancient-percolating-lantern.md §A4.4
 */
export function UnitPicker({ data, message, onAction, readOnly = false, onUpdateData, orgId, conversationId }: UnitPickerProps): React.ReactElement {
  const [filterOpen, setFilterOpen] = useState(false);

  // Selected state — message has actionTaken.
  if (message.actionTaken) {
    const action = message.actionTaken as StructuredActionTaken;
    if (action.type === "selected" && typeof action.target === "string") {
      const snapshot = action.snapshot as UnitOption | undefined;
      const picked = snapshot ?? data.units.find((u) => u.id === action.target);
      if (picked) {
        return <SelectedUnitCard unit={picked} readOnly={readOnly} />;
      }
    }
    if (action.type === "dismissed") {
      return <DismissedNote time={action.takenAt ? formatTime(action.takenAt) : ""} readOnly={readOnly} label="No unit selected" />;
    }
  }

  const showFilterButton = !readOnly && onUpdateData && orgId !== undefined;

  // Empty results — filter recap card + centered "no units" card so the
  // customer can edit filters or reset. Per Figma node 207-19339.
  if (data.units.length === 0) {
    const summary = summarizeActiveFilters(data.filters);
    return (
      <div className="flex w-full flex-col gap-2">
        {showFilterButton && <FilterSummaryCard summary={summary.length > 0 ? summary : "No filters applied"} onEdit={() => setFilterOpen(true)} />}
        <EmptyUnitsCard />
        {filterOpen && onUpdateData && orgId !== undefined && (
          <FilterModal
            data={data}
            messageId={message.messageId}
            orgId={orgId}
            conversationId={conversationId}
            onClose={() => setFilterOpen(false)}
            onUpdateData={onUpdateData}
          />
        )}
      </div>
    );
  }

  // Full-embed: fills its parent (the takeover surface in ChatWindow,
  // which owns the FunnelHeader + Stepper above this body). No outer
  // border — cards stack with gap-2 against the grey-50 takeover bg.
  if (data.presentation === "fullEmbed") {
    return (
      <div className="flex flex-col gap-2">
        {showFilterButton && (
          <FullEmbedFilterRow
            onOpen={() => setFilterOpen(true)}
            activeCount={countActiveFilters(data.filters)}
            summary={summarizeActiveFilters(data.filters)}
          />
        )}
        <div className="flex flex-col gap-2">
          {data.units.map((unit) => (
            <FullEmbedUnitCard
              key={unit.id}
              unit={unit}
              currencyCode={data.currencyCode}
              readOnly={readOnly}
              onTap={readOnly ? undefined : () => onAction?.(buildSelectedAction(unit))}
              reserveShortcut={!!data.reserveShortcut}
              onReserve={readOnly ? undefined : () => onAction?.({ ...buildSelectedAction(unit), intent: "reserve" })}
            />
          ))}
        </div>
        {filterOpen && onUpdateData && orgId !== undefined && (
          <FilterModal
            data={data}
            messageId={message.messageId}
            orgId={orgId}
            conversationId={conversationId}
            onClose={() => setFilterOpen(false)}
            onUpdateData={onUpdateData}
          />
        )}
      </div>
    );
  }

  // Inline card (default for non-fullEmbed emissions).
  return (
    <div className="rounded-2xl border border-grey-200 bg-white p-3">
      {showFilterButton && (
        <FilterRow
          data={data}
          onOpen={() => setFilterOpen(true)}
          currentSummary={`${data.units.length} unit${data.units.length === 1 ? "" : "s"} at ${data.locationName || `location ${data.locationId}`}`}
        />
      )}
      <div className="flex flex-col gap-2">
        {data.units.map((unit) => (
          <UnitCard
            key={unit.id}
            unit={unit}
            currencyCode={data.currencyCode}
            readOnly={readOnly}
            onTap={readOnly ? undefined : () => onAction?.(buildSelectedAction(unit))}
            reserveShortcut={!!data.reserveShortcut}
            onReserve={readOnly ? undefined : () => onAction?.({ ...buildSelectedAction(unit), intent: "reserve" })}
          />
        ))}
      </div>
      {filterOpen && onUpdateData && orgId !== undefined && (
        <FilterModal
          data={data}
          messageId={message.messageId}
          orgId={orgId}
          conversationId={conversationId}
          onClose={() => setFilterOpen(false)}
          onUpdateData={onUpdateData}
        />
      )}
    </div>
  );
}

function buildSelectedAction(unit: UnitOption): StructuredActionTaken {
  return {
    type: "selected",
    takenAt: new Date().toISOString(),
    target: unit.id,
    snapshot: unit
  };
}

interface FilterRowProps {
  data: UnitPickerData;
  onOpen: () => void;
  currentSummary: string;
}

/** "Filter" button row above the unit list. Per Figma node 276-7500. */
function FilterRow({ data, onOpen, currentSummary }: FilterRowProps): React.ReactElement {
  const activeCount = countActiveFilters(data.filters);
  return (
    <div className="mb-2 flex items-center justify-between border-b border-grey-100 pb-2">
      <div className="text-xs font-medium tracking-wide text-grey-500 uppercase">{currentSummary}</div>
      <button
        type="button"
        onClick={onOpen}
        className="flex items-center gap-1 rounded border border-grey-200 px-2 py-1 text-xs font-medium text-grey-700 hover:bg-grey-50"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="4" y1="6" x2="20" y2="6" />
          <line x1="7" y1="12" x2="17" y2="12" />
          <line x1="10" y1="18" x2="14" y2="18" />
        </svg>
        Filter
        {activeCount > 0 && <span className="rounded-full bg-primary-500 px-1.5 text-[10px] font-semibold text-white">{activeCount}</span>}
      </button>
    </div>
  );
}

function countActiveFilters(f: UnitFilters): number {
  let n = 0;
  if (f.sizeBucket) n++;
  if (f.climateOnly) n++;
  if (f.driveUpOnly) n++;
  return n;
}

interface FilterModalProps {
  data: UnitPickerData;
  messageId: string;
  orgId: number;
  conversationId?: number | null;
  onClose: () => void;
  onUpdateData: (data: Record<string, unknown>) => void;
}

/**
 * Bottom-sheet filter modal — main list (Unit Size, Amenities) pushes
 * left into a per-field detail panel. Per Figma nodes 207-17843 (main)
 * and 207-18057 (detail).
 */
const FILTER_MODAL_TRANSITION_MS = 300;
const SIZE_BUCKETS = ["small", "medium", "large", "extra-large"] as const;
type DetailPanel = "size" | "amenities";

function FilterModal({ data, messageId, orgId, conversationId, onClose, onUpdateData }: FilterModalProps): React.ReactElement {
  const [sizeBucket, setSizeBucket] = useState<UnitFilters["sizeBucket"]>(data.filters.sizeBucket);
  const [climateOnly, setClimateOnly] = useState(!!data.filters.climateOnly);
  const [driveUpOnly, setDriveUpOnly] = useState(!!data.filters.driveUpOnly);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Enter on next frame so the initial off-screen/transparent styles get
  // committed before transitioning to the visible state.
  const [entered, setEntered] = useState(false);
  const [closing, setClosing] = useState(false);
  // Active panel — null = main list; otherwise which detail to show.
  const [activeDetail, setActiveDetail] = useState<DetailPanel | null>(null);
  // Kept in sync so the back-slide animation continues to render the panel
  // the user just left until the transition completes.
  const [currentDetail, setCurrentDetail] = useState<DetailPanel>("size");

  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const requestClose = (): void => {
    if (closing) return;
    setClosing(true);
    setEntered(false);
    window.setTimeout(onClose, FILTER_MODAL_TRANSITION_MS);
  };

  const openDetail = (panel: DetailPanel): void => {
    setCurrentDetail(panel);
    setActiveDetail(panel);
  };

  const backToMain = (): void => setActiveDetail(null);

  const apply = async (next: UnitFilters): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      // Strip undefined / falsey entries so the api-helper doesn't serialize
      // them as `?key=undefined` (which then fails server-side TSOA validation).
      const params: Record<string, unknown> = {
        sourceMessageId: messageId,
        sourceAction: "filter_change"
      };
      if (next.sizeBucket) params["sizeBucket"] = next.sizeBucket;
      if (next.climateOnly) params["climateOnly"] = true;
      if (next.driveUpOnly) params["driveUpOnly"] = true;
      if (conversationId !== null && conversationId !== undefined) params["botConversationId"] = conversationId;
      const result = await API.EmbedPicker.getUnits(orgId, parseInt(data.locationId, 10), params);
      // Preserve header fields (presentation + address) and the Reserve redirect
      // (ENG-3258) — they're stable for a given location and shouldn't flicker
      // or vanish when the picker controller's best-effort var lookups miss.
      // This merge replaces the persisted payload wholesale, so anything not
      // carried over here is gone for good.
      const merged: Record<string, unknown> = {
        ...result,
        ...(data.presentation ? { presentation: data.presentation } : {}),
        ...(!result.locationAddress && data.locationAddress ? { locationAddress: data.locationAddress } : {}),
        ...(!result.reservationUrl && data.reservationUrl ? { reservationUrl: data.reservationUrl } : {}),
        ...(!result.currencyCode && data.currencyCode ? { currencyCode: data.currencyCode } : {})
      };
      onUpdateData(merged);
      requestClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Couldn't apply filters";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleApply = (): void => {
    void apply({ sizeBucket, climateOnly, driveUpOnly });
  };

  const handleResetAll = (): void => {
    setSizeBucket(undefined);
    setClimateOnly(false);
    setDriveUpOnly(false);
    void apply({ sizeBucket: undefined, climateOnly: false, driveUpOnly: false });
  };

  const handleResetDetail = (): void => {
    if (currentDetail === "size") {
      setSizeBucket(undefined);
    } else {
      setClimateOnly(false);
      setDriveUpOnly(false);
    }
  };

  const sizeSummary = sizeBucket ? sizeLabel(sizeBucket) : "Select";
  const amenitiesSummary = summarizeAmenities(
    climateOnly ? featureName(data, FacilityAndSpaceFeatures.ClimateControl, "Climate controlled") : null,
    driveUpOnly ? featureName(data, FacilityAndSpaceFeatures.DriveUp, "Drive-up access") : null
  );
  const showingDetail = activeDetail !== null;

  const detailTitle = currentDetail === "size" ? "Unit Size" : "Amenities";

  return (
    <div
      className={filterClasses(
        "absolute inset-0 z-100 flex items-end justify-center transition-colors duration-200 ease-out",
        entered ? "bg-black/40" : "bg-black/0"
      )}
      onClick={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        className={filterClasses(
          "w-full overflow-hidden rounded-t-xl bg-white shadow-2xl transition-transform duration-300 ease-out will-change-transform",
          entered ? "translate-y-0" : "translate-y-full"
        )}
      >
        {/* Header stays put — title slot crossfades between "Filter" and the
            back button; the close button never moves. */}
        <div className="flex w-full items-center justify-between p-4">
          <div className="grid">
            <span
              className={filterClasses(
                "col-start-1 row-start-1 self-center text-base font-semibold text-grey-900 transition-opacity duration-200",
                showingDetail ? "pointer-events-none opacity-0" : "opacity-100"
              )}
            >
              Filter
            </span>
            <button
              type="button"
              onClick={backToMain}
              aria-label="Back"
              disabled={!showingDetail}
              className={filterClasses(
                "col-start-1 row-start-1 flex cursor-pointer items-center gap-1 self-center text-base font-semibold text-grey-900 transition-opacity duration-200",
                showingDetail ? "opacity-100" : "pointer-events-none opacity-0"
              )}
            >
              <ChevronLeftIcon />
              <span>{detailTitle}</span>
            </button>
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="flex size-8 cursor-pointer items-center justify-center rounded-full bg-grey-100 text-grey-700 hover:bg-grey-200"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Sliding body — main and detail panels share a grid cell; one
            sits in view while the other waits off-screen. */}
        <div className="overflow-hidden">
          <div className="grid">
            <div
              className={filterClasses(
                "col-start-1 row-start-1 flex flex-col gap-3 px-4 transition-transform duration-300 ease-out will-change-transform",
                showingDetail ? "-translate-x-full" : "translate-x-0"
              )}
              aria-hidden={showingDetail}
            >
              <FilterRowItem
                label="Unit Size"
                value={sizeSummary}
                onClick={() => openDetail("size")}
                disabled={busy || showingDetail}
                selected={!!sizeBucket}
              />
              <FilterRowItem
                label="Amenities"
                value={amenitiesSummary}
                onClick={() => openDetail("amenities")}
                disabled={busy || showingDetail}
                selected={climateOnly || driveUpOnly}
              />
            </div>
            <div
              className={filterClasses(
                "col-start-1 row-start-1 flex flex-col px-4 transition-transform duration-300 ease-out will-change-transform",
                showingDetail ? "translate-x-0" : "translate-x-full"
              )}
              aria-hidden={!showingDetail}
            >
              {currentDetail === "size" &&
                SIZE_BUCKETS.map((bucket) => (
                  <CheckboxRow
                    key={bucket}
                    label={sizeLabel(bucket)}
                    checked={sizeBucket === bucket}
                    onToggle={() => setSizeBucket(sizeBucket === bucket ? undefined : bucket)}
                    disabled={busy || !showingDetail}
                  />
                ))}
              {currentDetail === "amenities" && (
                <>
                  <CheckboxRow
                    label={featureName(data, FacilityAndSpaceFeatures.ClimateControl, "Climate controlled")}
                    checked={climateOnly}
                    onToggle={() => setClimateOnly((v) => !v)}
                    disabled={busy || !showingDetail}
                  />
                  <CheckboxRow
                    label={featureName(data, FacilityAndSpaceFeatures.DriveUp, "Drive-up access")}
                    checked={driveUpOnly}
                    onToggle={() => setDriveUpOnly((v) => !v)}
                    disabled={busy || !showingDetail}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {error && <div className="px-4 pt-2 text-xs text-red-600">{error}</div>}

        {/* Footer stays put — Reset/Apply and Reset/Confirm crossfade in
            the same grid cell. */}
        <div className="grid">
          <div
            className={filterClasses(
              "col-start-1 row-start-1 flex items-center gap-2 p-4 transition-opacity duration-200",
              showingDetail ? "pointer-events-none opacity-0" : "opacity-100"
            )}
            aria-hidden={showingDetail}
          >
            <button
              type="button"
              onClick={handleResetAll}
              disabled={busy || showingDetail}
              className="h-10 flex-1 cursor-pointer rounded border border-grey-300 px-3 text-base font-semibold text-grey-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={busy || showingDetail}
              className="h-10 flex-1 cursor-pointer rounded bg-primary-500 px-3 text-base font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? "Applying..." : "Apply"}
            </button>
          </div>
          <div
            className={filterClasses(
              "col-start-1 row-start-1 flex items-center gap-2 p-4 transition-opacity duration-200",
              showingDetail ? "opacity-100" : "pointer-events-none opacity-0"
            )}
            aria-hidden={!showingDetail}
          >
            <button
              type="button"
              onClick={handleResetDetail}
              disabled={busy || !showingDetail}
              className="h-10 flex-1 cursor-pointer rounded border border-grey-300 px-3 text-base font-semibold text-grey-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={backToMain}
              disabled={busy || !showingDetail}
              className="h-10 flex-1 cursor-pointer rounded bg-grey-900 px-3 text-base font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface FilterRowItemProps {
  label: string;
  value: string;
  onClick: () => void;
  disabled?: boolean;
  /** True once the user has picked a value — darkens it from the placeholder grey. */
  selected?: boolean;
}

function FilterRowItem({ label, value, onClick, disabled, selected }: FilterRowItemProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={filterClasses(
        "flex w-full items-center gap-2 rounded border border-grey-200 bg-gradient-to-b from-white to-grey-50 px-3 py-2 text-left",
        disabled ? "opacity-50" : "cursor-pointer"
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-semibold text-grey-600">{label}</span>
        <span className={filterClasses("text-base", selected ? "text-grey-900" : "text-grey-500")}>{value}</span>
      </div>
      <ChevronRightIcon />
    </button>
  );
}

interface CheckboxRowProps {
  label: string;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

function CheckboxRow({ label, checked, onToggle, disabled }: CheckboxRowProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={filterClasses("flex w-full items-center gap-2 rounded bg-white px-2 py-3 text-left", disabled ? "opacity-50" : "cursor-pointer")}
    >
      <span
        aria-hidden
        className={filterClasses(
          "flex size-[18px] shrink-0 items-center justify-center rounded-[2px] border",
          checked ? "border-primary-500 bg-primary-500" : "border-grey-400 bg-white"
        )}
      >
        {checked && <CheckmarkIcon />}
      </span>
      <span className="text-base text-grey-900">{label}</span>
    </button>
  );
}

function ChevronLeftIcon(): React.ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function ChevronRightIcon(): React.ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0 text-grey-500"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function CloseIcon(): React.ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

function CheckmarkIcon(): React.ReactElement {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-white"
    >
      <path d="M5 12l5 5 9-11" />
    </svg>
  );
}

function summarizeAmenities(climateLabel: string | null, driveUpLabel: string | null): string {
  const parts = [climateLabel, driveUpLabel].filter((l): l is string => Boolean(l));
  return parts.length > 0 ? parts.join(", ") : "Select";
}

/**
 * The operator's display name for a feature this picker labels outside a unit card (the
 * filter checkboxes), from the payload; the catalog fallback keeps older payloads readable.
 */
function featureName(data: UnitPickerData, feature: FacilityAndSpaceFeatures, fallback: string): string {
  return data.featureNames?.[feature] ?? fallback;
}

function sizeLabel(s: NonNullable<UnitFilters["sizeBucket"]>): string {
  if (s === "small") return "Small (5×5–5×10)";
  if (s === "medium") return "Medium (10×10)";
  if (s === "large") return "Large (10×15–10×20)";
  return "XL (10×25+)";
}

function compactSizeLabel(s: NonNullable<UnitFilters["sizeBucket"]>): string {
  if (s === "small") return "Small";
  if (s === "medium") return "Medium";
  if (s === "large") return "Large";
  return "XL";
}

function summarizeActiveFilters(f: UnitFilters): string {
  const parts: string[] = [];
  if (f.sizeBucket) parts.push(compactSizeLabel(f.sizeBucket));
  if (f.climateOnly) parts.push("Climate controlled");
  if (f.driveUpOnly) parts.push("Drive-up");
  return parts.join(", ");
}

interface FilterSummaryCardProps {
  summary: string;
  onEdit: () => void;
}

/**
 * Filter recap card shown above the units list / empty state. Displays
 * the active filter selections in plain text with an "Edit" text-button
 * on the right that opens the filter modal. Per Figma node 207-19370.
 */
function FilterSummaryCard({ summary, onEdit }: FilterSummaryCardProps): React.ReactElement {
  return (
    <div className="flex w-full items-center gap-2 rounded-lg border border-grey-200 bg-white px-3 py-2">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-sm font-semibold text-grey-600">Filter</span>
        <span className="truncate text-base text-grey-900">{summary}</span>
      </div>
      <button type="button" onClick={onEdit} className="text-sm font-semibold text-primary-500">
        Edit
      </button>
    </div>
  );
}

/**
 * Centered "no units available" card with a SearchX icon, headline, and
 * helper text. Per Figma node 207-19375.
 */
function EmptyUnitsCard(): React.ReactElement {
  return (
    <div className="flex w-full flex-col items-center justify-center gap-1 rounded-lg border border-grey-200 bg-white p-3">
      <div className="flex size-8 items-center justify-center rounded bg-grey-50 text-grey-900">
        <SearchXIcon />
      </div>
      <span className="text-base font-semibold text-grey-900">No units available</span>
      <span className="text-center text-sm text-grey-600">Try resetting the filter</span>
    </div>
  );
}

function SearchXIcon(): React.ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
      <path d="m8.5 8.5 5 5" />
      <path d="m13.5 8.5-5 5" />
    </svg>
  );
}

interface UnitCardProps {
  unit: UnitOption;
  /** ISO 4217 code from the payload (`UnitPickerData.currencyCode`). */
  currencyCode?: string;
  readOnly: boolean;
  onTap?: () => void;
  /** ENG-3363 — show a Reserve button that jumps to the rental page (the script in force has that step). */
  reserveShortcut?: boolean;
  onReserve?: () => void;
}

function UnitCard({ unit, currencyCode, readOnly, onTap, reserveShortcut, onReserve }: UnitCardProps): React.ReactElement {
  const sizeLine = formatSize(unit);
  // Full amenity list (same source as the full-embed card), not just
  // climate/drive-up. See unitFeaturesToLabels.
  const featureLine = unitFeaturesToLabels(unit).join(", ");
  const tappable = !readOnly && onTap;

  if (tappable) {
    return (
      <button
        type="button"
        onClick={onTap}
        className="flex w-full flex-col rounded-xl border border-grey-200 bg-white px-3 py-2 text-left hover:border-primary-300 hover:bg-primary-50"
      >
        <span className="text-sm font-medium text-grey-900">{unit.name}</span>
        {sizeLine && <span className="text-xs text-grey-500">{sizeLine}</span>}
        {featureLine && <span className="text-[11px] text-grey-400">{featureLine}</span>}
        {typeof unit.rate === "number" && (
          <span className="mt-0.5 text-sm font-semibold text-primary-600">{formatCurrency(unit.rate, currencyCode)}/mo</span>
        )}
        {unit.pricingNote && <span className="mt-0.5 text-[11px] text-grey-500 italic">{unit.pricingNote}</span>}
        {reserveShortcut && onReserve && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onReserve();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onReserve();
              }
            }}
            className="mt-1.5 inline-flex w-fit rounded-md bg-primary-500 px-3 py-1 text-xs font-semibold text-white hover:bg-primary-600"
          >
            {t("reservation.step1.reserve")}
          </span>
        )}
      </button>
    );
  }

  // In the console this is a record of what was offered, so the two facts an
  // agent scans for — which unit and what it costs — go black, everything else
  // settles on grey-600, and the type moves onto the design-system tokens.
  //
  // The tokens are console-only on purpose: `.body-*` is defined in
  // libs/ui/styles.css, which the widget never loads, so applying them there
  // would silently drop the sizing. The widget keeps its raw Tailwind.
  const titleClasses = readOnly ? "body-sm-alt text-grey-900" : "text-sm font-medium text-grey-700";
  const sizeClasses = readOnly ? "body-sm text-grey-600" : "text-xs text-grey-500";
  const featureClasses = readOnly ? "body-sm text-grey-600" : "text-[11px] text-grey-400";
  const rateClasses = readOnly ? "body-sm-alt text-grey-900" : "text-sm font-semibold text-grey-700";

  return (
    <div className="flex flex-col rounded-xl border border-grey-200 bg-grey-50 px-3 py-2">
      <span className={titleClasses}>{unit.name}</span>
      {sizeLine && <span className={sizeClasses}>{sizeLine}</span>}
      {featureLine && <span className={featureClasses}>{featureLine}</span>}
      {typeof unit.rate === "number" && <span className={filterClasses("mt-0.5", rateClasses)}>{formatCurrency(unit.rate, currencyCode)}/mo</span>}
    </div>
  );
}

function SelectedUnitCard({ unit, readOnly }: { unit: UnitOption; readOnly: boolean }): React.ReactElement {
  const muted = readOnly ? "text-grey-600" : "text-grey-500";
  // Titled "Selected Unit" card — Figma Chat-Invisibles node 72:8211.
  const sizeLabel = formatSize(unit) || unit.name;
  // Full amenity list (e.g. "Drive up, Floor 3, Climate Controlled, RV
  // Parking") — same source the unit-list card uses.
  const featureLine = unitFeaturesToLabels(unit).join(", ");
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-grey-200 bg-white">
      <div className="border-b border-grey-200 px-3 py-2">
        <span className={filterClasses("text-sm font-semibold", muted)}>Selected Unit</span>
      </div>
      <div className="flex flex-col p-3">
        <span className="font-heading text-lg leading-6 font-semibold text-grey-900">{sizeLabel}</span>
        {featureLine && <span className={filterClasses("truncate text-sm", muted)}>{featureLine}</span>}
      </div>
    </div>
  );
}

function DismissedNote({ time, readOnly, label: labelText }: { time?: string; readOnly: boolean; label: string }): React.ReactElement {
  // Figma Chat-Invisibles node 90:1660. In the console this is one more
  // internal row, so it takes the shared pill's 4px radius, grey-600 text and
  // mono token; the widget keeps the original treatment.
  const label = time ? `${labelText} • ${time}` : labelText;
  return (
    <div className="flex justify-center">
      <div
        className={filterClasses(
          "inline-flex items-center gap-2 bg-grey-50 px-2 py-1",
          readOnly ? "rounded-sm text-grey-600" : "rounded text-grey-500"
        )}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="shrink-0" aria-hidden="true">
          <circle cx="8" cy="8" r="6.5" />
          <line x1="8" y1="7" x2="8" y2="11" strokeLinecap="round" />
          <circle cx="8" cy="5" r="0.75" fill="currentColor" stroke="none" />
        </svg>
        <span className={readOnly ? "body-mono" : "font-mono text-xs leading-4"}>{label}</span>
      </div>
    </div>
  );
}

function formatSize(unit: UnitOption): string {
  if (unit.dimensions) return unit.dimensions;
  if (unit.width && unit.length) return `${unit.width} × ${unit.length} ft`;
  return "";
}

/**
 * Display-ready feature labels for a unit. Prefers `unit.featureLabels`, the
 * names the producer resolved from the location's variables (the operator's
 * rename when set) — the widget has no variables and must not name a feature
 * itself (ENG-3209). Older payloads without labels fall back to the catalog
 * label per key, and payloads without a `features` list fall back to the two
 * boolean flags.
 */
export function unitFeaturesToLabels(unit: UnitOption): string[] {
  if (unit.features && unit.features.length > 0) {
    if (unit.featureLabels && unit.featureLabels.length === unit.features.length) {
      return unit.featureLabels.filter((l) => Boolean(l));
    }
    return unit.features.map((key) => findFeatureDef(key)?.label ?? "").filter((l): l is string => Boolean(l));
  }
  const labels: string[] = [];
  if (unit.driveUp) labels.push("Drive-up");
  if (unit.climateControlled) labels.push("Climate controlled");
  return labels;
}

interface FullEmbedFilterRowProps {
  onOpen: () => void;
  activeCount: number;
  /** Comma-joined summary of active filters (e.g. "5x10, Drive-up"). */
  summary: string;
}

/**
 * Step 1 filter trigger — white bordered card with a "Filter" label on the
 * left. When filters are active it shows the selection summary beneath the
 * label (wrapping, so the card grows with the content) and the action reads
 * "Edit"; with no filters it just prompts "Add". Per Figma 370:21584 (empty)
 * and 279:10297 (with selections). Tapping anywhere opens the FilterModal.
 */
function FullEmbedFilterRow({ onOpen, activeCount, summary }: FullEmbedFilterRowProps): React.ReactElement {
  const hasFilters = activeCount > 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full cursor-pointer items-center gap-2 rounded-lg border border-grey-200 bg-white px-3 py-2 text-left hover:border-grey-300"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-semibold text-grey-500">{t("reservation.step1.filter")}</span>
        {hasFilters && <span className="text-base leading-snug break-words text-grey-900">{summary}</span>}
      </span>
      <span className="shrink-0 self-center text-sm font-semibold text-primary-500">{hasFilters ? "Edit" : t("reservation.step1.filterAdd")}</span>
    </button>
  );
}

interface FullEmbedUnitCardProps {
  unit: UnitOption;
  /** ISO 4217 code from the payload (`UnitPickerData.currencyCode`). */
  currencyCode?: string;
  readOnly: boolean;
  onTap?: () => void;
  /** ENG-3363 — the button reads Reserve and jumps to the rental page; otherwise it reads Select and picks. */
  reserveShortcut?: boolean;
  onReserve?: () => void;
}

/**
 * Step 1 unit card — thumbnail + size + info icon + features on the
 * left, "from $X" + Reserve button on the right. Per Figma 276:7685.
 * The whole card is interactive but the Reserve button repeats the
 * action for affordance; both fire the same `onTap` (the selected action
 * for this unit).
 */
function FullEmbedUnitCard({ unit, currencyCode, readOnly, onTap, reserveShortcut, onReserve }: FullEmbedUnitCardProps): React.ReactElement {
  const sizeLabel = unit.dimensions || formatSize(unit) || unit.name;
  const featureLabels = unitFeaturesToLabels(unit);
  const tappable = !readOnly && !!onTap;
  const shortcut = !!reserveShortcut && !!onReserve;
  // ENG-3363 — the card tap always picks (the conversation continues under the script). The button
  // is a Reserve shortcut only when the script has the rental-page step; otherwise it just picks.
  const handleReserve = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (shortcut) onReserve?.();
    else onTap?.();
  };
  return (
    <div
      role={tappable ? "button" : undefined}
      tabIndex={tappable ? 0 : undefined}
      onClick={tappable ? onTap : undefined}
      onKeyDown={
        tappable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onTap?.();
              }
            }
          : undefined
      }
      className={filterClasses(
        "flex items-center gap-2 rounded-lg border border-grey-200 bg-white p-3",
        tappable && "cursor-pointer hover:border-grey-300"
      )}
    >
      <UnitThumbnail imageUrl={unit.imageUrl} kind={unitImageKindFor(unit)} alt={sizeLabel} />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-1">
          <span className="font-heading text-lg leading-6 font-semibold text-grey-900">{sizeLabel}</span>
          <InfoCircleIcon />
        </div>
        {featureLabels.length > 0 && <p className="text-sm leading-tight text-grey-600">{featureLabels.join(", ")}</p>}
      </div>
      <div className="flex shrink-0 flex-col items-center gap-1">
        {typeof unit.rate === "number" && (
          <div className="flex items-center gap-1 whitespace-nowrap">
            <span className="text-xs font-semibold text-grey-600">{t("reservation.step1.from")}</span>
            <span className="font-heading text-lg leading-6 font-semibold text-primary-500">{formatCurrency(unit.rate, currencyCode)}</span>
          </div>
        )}
        {tappable && (
          <button
            type="button"
            onClick={handleReserve}
            className="flex min-w-16 cursor-pointer items-center justify-center rounded border border-primary-500 bg-primary-500 px-2 py-0.5 text-base font-semibold text-white hover:bg-primary-600"
          >
            {shortcut ? t("reservation.step1.reserve") : t("reservation.step1.select")}
          </button>
        )}
      </div>
    </div>
  );
}

function InfoCircleIcon(): React.ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-grey-400" aria-hidden="true">
      <circle cx="8" cy="8" r="6.5" />
      <line x1="8" y1="7" x2="8" y2="11" strokeLinecap="round" />
      <circle cx="8" cy="5" r="0.75" fill="currentColor" stroke="none" />
    </svg>
  );
}
